import { useQueries, useQuery } from '@tanstack/react-query';
import { clientGetFileAttachmentsForItem, useGraphqlClient } from '../graphql';
import { fileAttachmentQueryKeys } from './queryKeys';

interface UseFileAttachmentsForItemArgs {
  itemID: string | undefined | null;
  tenantID: string | undefined | null;
}

export const useFileAttachmentsForItem = ({ itemID, tenantID }: UseFileAttachmentsForItemArgs) => {
  const gqlClient = useGraphqlClient();

  const fileAttachmentsQuery = useQuery({
    enabled: !!itemID && !!tenantID,
    queryKey: fileAttachmentQueryKeys.fileAttachments(tenantID, itemID),
    queryFn: () => {
      if (itemID && tenantID)
        return clientGetFileAttachmentsForItem(gqlClient, {
          id: itemID,
          tenantID,
        });
    },
  });

  return {
    fileAttachments: fileAttachmentsQuery.data?.items || [],
    fileAttachmentsQuery,
  };
};

interface UseFileAttachmentsForItemsArgs {
  itemIDs: string[];
  tenantID: string | undefined | null;
}

export const useFileAttachmentsForItems = ({ itemIDs, tenantID }: UseFileAttachmentsForItemsArgs) => {
  const gqlClient = useGraphqlClient();
  const uniqueItemIDs = Array.from(new Set(itemIDs));
  const fileAttachmentQueries = useQueries({
    queries: uniqueItemIDs.map((itemID) => ({
      enabled: !!tenantID,
      queryKey: fileAttachmentQueryKeys.fileAttachments(tenantID, itemID),
      queryFn: () => {
        if (!tenantID) throw new Error('Tenant ID is required to load file attachments.');
        return clientGetFileAttachmentsForItem(gqlClient, { id: itemID, tenantID });
      },
    })),
  });

  return {
    fileAttachments: fileAttachmentQueries.flatMap((query) => query.data?.items ?? []),
    fileAttachmentQueries,
  };
};
